/**
 * @file order-menu.ts
 *
 * The Orders list row contextual-menu action set and the geometry used to anchor
 * the desktop menu near its trigger. View and Delete are always present; Cancel is
 * state-gated to a cancellable order and Refund to a fully paid one (`/v1` has no
 * generic status-set endpoint, so the legacy Edit action stays replaced by Cancel /
 * Refund). Delete is offered on every order but the engine gates it to
 * `cancelled` / `completed` orders. Split from the pure `order-list` helpers because it
 * references localized messages (`adminMessages`), which the pure formatting helpers
 * must stay free of.
 */

import type { ShopOrder } from '../api/shop-types';
import type { ContextMenuItem } from '../components/ContextMenu';
import { adminMessages } from '../i18n/messages';
import { canCancelOrder, canRefundOrder } from './order-list';

/** Row contextual-menu actions for an order. */
export type OrderMenuAction = 'view' | 'cancel' | 'refund' | 'delete';

/** Geometry for anchoring the desktop contextual menu near its trigger. */
export const ORDER_MENU_WIDTH = 300;
export const ORDER_MENU_HEIGHT = 162;
export const ORDER_MENU_VIEWPORT_MARGIN = 8;

/**
 * Builds the contextual-menu actions available for an order: View and Delete always,
 * plus Cancel and/or Refund depending on the order's state. Delete is shown on every
 * order — the engine gates it to `cancelled` / `completed` and the screen surfaces the
 * `order_not_deletable` error otherwise.
 *
 * @param order - The order the menu is for.
 *
 * @returns The applicable menu items.
 */
export function orderMenuActions(order: ShopOrder): ContextMenuItem<OrderMenuAction>[] {
  const items: ContextMenuItem<OrderMenuAction>[] = [
    { value: 'view', iconName: 'visibility', label: adminMessages.ordersMenuView },
  ];
  if (canCancelOrder(order)) {
    items.push({ value: 'cancel', iconName: 'cancel', label: adminMessages.ordersMenuCancel });
  }
  if (canRefundOrder(order)) {
    items.push({ value: 'refund', iconName: 'undo', label: adminMessages.ordersMenuRefund });
  }
  items.push({ value: 'delete', iconName: 'delete', label: adminMessages.ordersMenuDelete });
  return items;
}
